import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { addRemoteTaskIdAction, markTaskAsFailedAction, setTaskAction } from '~/store/tasks/actions';
import { type CreateTaskParams } from '~/store/tasks/types';
import { reportErrorToBugsnag } from '~/utils/ErrorUtils';
import { addTasksToLocalStorage, createLocalTaskId } from '~/utils/taskUtils/storage';

export type { CreateTaskParams } from '~/store/tasks/types';

export const useCreateTask = () => {
  const dispatch = useDispatch();

  const createTask = useCallback(
    async ({ task, createTask, immediateStart = true }: CreateTaskParams) => {
      /** We generate a temporary ID because we want to show something in the UI immediately */
      const localTaskId = createLocalTaskId();

      dispatch(
        setTaskAction({
          localTaskId,
          remoteTaskId: undefined,
          status: immediateStart ? 'in-progress' : 'pending',
          hidden: false,
          ...task,
        }),
      );

      try {
        const remoteTask = await createTask();
        const remoteTaskId = remoteTask.id;

        addTasksToLocalStorage([
          {
            localTaskId,
            remoteTaskId,
            status: immediateStart ? 'in-progress' : 'pending',
            hidden: false,
            ...task,
          },
        ]);

        dispatch(
          addRemoteTaskIdAction({
            localTaskId,
            remoteTaskId,
          }),
        );

        return { data: { localTaskId, remoteTaskId } };
      } catch (error) {
        reportErrorToBugsnag({
          error,
          context: `Failed to create task`,
          metadata: { task },
        });

        dispatch(markTaskAsFailedAction({ localTaskId }));
        return { error: error as Error, localTaskId };
      }
    },
    [dispatch],
  );

  return {
    createTask,
  };
};
